import type { InteractContext } from "../interact-context.ts";
import type { PageRun } from "../run/page.ts";
import type { ToolBelt } from "../belt/store.ts";
import type { StanceStore } from "../fight/stance.ts";
import type { TouchView } from "./actions.ts";
import { interactViewInto, newInteractView, type MutableInteractView } from "./interact-view.ts";

/**
 * What the touch buttons read of the page each frame (ADR-061's amendment of 2026-10-31, decision 7): the Interact button's
 * view from the choice E would make, whether Sprint is latched, whether the weapon is drawn and whether a tool is in
 * hand. One view object is kept and written in place, so a frame of steady play allocates nothing.
 */

/**
 * What the reader asks of the page.
 */
export interface TouchViewSources {
  readonly context: () => InteractContext;
  readonly run: () => Pick<PageRun, "touched"> | undefined;
  readonly stance: () => Pick<StanceStore, "getSnapshot">;
  readonly belt: () => Pick<ToolBelt, "getSnapshot"> | undefined;
}

/**
 * A `TouchView` written in place.
 */
interface MutableTouchView {
  readonly interact: MutableInteractView;
  sprint: boolean;
  drawn: boolean;
  toolInHand: boolean;
}

/**
 * Reads the touch view from the page.
 */
export class TouchViewReader {
  private readonly view: MutableTouchView = { interact: newInteractView(), sprint: false, drawn: false, toolInHand: false };

  /**
   * @param sources What the reader asks of the page.
   */
  public constructor(private readonly sources: TouchViewSources) {}

  /**
   * The view now, the same object every time.
   *
   * @returns The view.
   */
  public read(): TouchView {
    const view = this.view;
    const sources = this.sources;
    interactViewInto(view.interact, sources.context());
    view.sprint = sources.run()?.touched === true;
    view.drawn = sources.stance().getSnapshot() === "drawn";
    const belt = sources.belt();
    view.toolInHand = belt !== undefined && belt.getSnapshot().record.selected !== 0;
    return view;
  }
}
